import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Loader2, Eye, Printer, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  PICKING: 'bg-amber-100 text-amber-800',
  DISTRIBUTING: 'bg-blue-100 text-blue-800',
  ASSEMBLING: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-stone-200 text-stone-700',
  FAILED: 'bg-rose-100 text-rose-800',
};

export default function ComboV2PlanList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const tk = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/combo-v2/plans?limit=100`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setPlans(res.data.plans || []);
    } catch (e) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCancel = async (planId) => {
    if (!window.confirm(`Cancel plan ${planId}? All un-picked bundles will be released.`)) return;
    try {
      setCancelling(planId);
      const tk = localStorage.getItem('session_token');
      const res = await axios.post(
        `${API}/api/combo-v2/plans/${planId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${tk}` } }
      );
      toast.success(
        `Cancelled. ${res.data.slips_cancelled} slip(s), ${res.data.bundles_released} bundle(s) released, ${res.data.sessions_cancelled} session(s).`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="combo-v2-plans-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/wms/combo-v2')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-3xl font-bold text-stone-900">Bulk Assembly Plans</h1>
          </div>
          <Button onClick={() => navigate('/wms/combo-v2/planner')} data-testid="new-plan-btn">
            <Plus className="h-4 w-4 mr-1" /> New Plan
          </Button>
        </div>

        <Card>
          {loading ? (
            <div className="p-12 text-center text-stone-500">
              <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
            </div>
          ) : !plans.length ? (
            <div className="p-12 text-center text-stone-500">
              No plans yet — create one from the planner.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="plans-table">
              <thead className="bg-stone-100">
                <tr>
                  <th className="text-left p-3 font-medium text-stone-700">Plan</th>
                  <th className="text-left p-3 font-medium text-stone-700">Name</th>
                  <th className="text-center p-3 font-medium text-stone-700">Status</th>
                  <th className="text-right p-3 font-medium text-stone-700">Sessions</th>
                  <th className="text-right p-3 font-medium text-stone-700">Slips</th>
                  <th className="text-right p-3 font-medium text-stone-700">Total Pcs</th>
                  <th className="text-left p-3 font-medium text-stone-700">Created</th>
                  <th className="text-center p-3 font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.plan_id} className="border-t hover:bg-stone-50" data-testid={`plan-row-${p.plan_id}`}>
                    <td className="p-3 font-mono text-xs">{p.plan_id}</td>
                    <td className="p-3">{p.plan_name}</td>
                    <td className="p-3 text-center">
                      <Badge className={STATUS_COLORS[p.status] || 'bg-stone-100'}>
                        {p.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">{p.sessions_count}</td>
                    <td className="p-3 text-right">{p.slips_count}</td>
                    <td className="p-3 text-right font-semibold">{p.total_qty}</td>
                    <td className="p-3 text-xs text-stone-500">
                      {p.created_at ? new Date(p.created_at).toLocaleString() : ''}
                      <div className="text-[11px] text-stone-400">{p.created_by}</div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/wms/combo-v2/plans/${p.plan_id}`)}
                          data-testid={`view-plan-${p.plan_id}`}
                          title="Open plan detail"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/wms/combo-v2/plans/${p.plan_id}/print`, '_blank', 'noopener,noreferrer')}
                          data-testid={`print-plan-${p.plan_id}`}
                          title="Print plan summary (A4)"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {p.status === 'PICKING' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancel(p.plan_id)}
                            disabled={cancelling === p.plan_id}
                            data-testid={`cancel-plan-${p.plan_id}`}
                            className="text-rose-600 hover:bg-rose-50"
                          >
                            {cancelling === p.plan_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
